// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useRef, useState } from 'react';

import { type PartCategory, type PartCatalog, type PartCategoryId, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Anchor, Box, Collapse, Flex, Stack, Text, Loader } from '@mantine/core';

import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { CreateCatalogCategoryModal } from 'modules/Shop/CatalogSetup/CreateCatalogCategoryModal';
import { EditCategoryModal } from 'modules/Shop/CatalogSetup/EditCategoryModal';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

type Props = {
  partCatalog: PartCatalog;
  categoryPath: CategoryPath;
  onCategorySelected: (category: PartCategory | null) => void;
  label?: string;
  allowCategoryCreation?: boolean;
  allowEditing?: boolean;
  refresh?: () => void;
  /** Used to display a loading symbol next to the "Categories" label */
  loading?: boolean;
  disableCategories?: PartCategoryId[];
};

type ChildProps = Props & {
  parentPartCategory?: PartCategory;
  padding?: number;
};

const navGap = 5;

const CatalogCategoryNavChild = ({
  parentPartCategory,
  padding = 0,
  refresh,
  allowCategoryCreation,
  ...props
}: ChildProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { partCatalog, categoryPath, onCategorySelected } = props;
  const selectedCategory = categoryPath[categoryPath.length - 1];

  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          partCatalogId: partCatalog.partCatalogId,
          parentPartCategoryId: parentPartCategory?.partCategoryId,
        },
      },
    }),
    // oxlint-disable-next-line react/preserve-manual-memoization
    [parentPartCategory?.partCategoryId, partCatalog.partCatalogId],
  );
  const {
    data: categories,
    entireCount,
    fetchNextPage,
    loading,
    reset,
    setDefaultOpts,
  } = useEvolvePaginatedGet('shop/partCatalog/category', defaultOpts);
  useEffect(() => setDefaultOpts(defaultOpts), [defaultOpts, setDefaultOpts]);

  const visible =
    isNil(parentPartCategory?.partCategoryId) ||
    categoryPath.some(({ partCategoryId }) => partCategoryId === parentPartCategory?.partCategoryId);

  useEffect(() => {
    if (categories.some((c) => c.partCategoryId === selectedCategory?.partCategoryId)) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [categories, selectedCategory?.partCategoryId]);

  return (
    <Box>
      {isNil(parentPartCategory) && entireCount === 0 && !loading && (
        <Text fz="sm" c="dimmed">
          {allowCategoryCreation ? <>Add a new category to get started.</> : <>No categories in selected catalog.</>}
        </Text>
      )}
      <Collapse ref={ref} expanded={categories.length > 0 && visible} display="flex" style={{ overflow: 'hidden' }}>
        <Stack flex={1} gap={navGap} style={{ alignItems: 'flex-start' }}>
          {categories.map((c) => {
            const selected = selectedCategory?.partCategoryId === c.partCategoryId;
            const partOfChain =
              selected || categoryPath.slice(0, -1).some(({ partCategoryId }) => partCategoryId === c.partCategoryId);
            const disabled = props.disableCategories?.includes(c.partCategoryId);
            return (
              <Stack key={c.partCategoryId} gap={partOfChain ? navGap : 0} w="100%" pl={padding}>
                {!disabled ? (
                  <>
                    <Anchor
                      fw={selected ? 'bold' : 'normal'}
                      display="block"
                      lh={1.2}
                      onClick={() => onCategorySelected(c)}
                      className="link"
                      c="dark"
                      style={{ userSelect: 'none' }}
                    >
                      <Flex align="flex-start" justify="space-between" gap="xs">
                        <Flex align="flex-start" gap={8}>
                          {c.name}
                          {selected && props.allowEditing && c.editable && (
                            <EditCategoryModal
                              partCatalog={partCatalog}
                              partCategory={c}
                              parentPartCategory={parentPartCategory ?? null}
                              onPartCategoryUpdated={(uc) => {
                                onCategorySelected(uc);
                                (refresh ?? reset)();
                              }}
                            />
                          )}
                        </Flex>
                        {c.partCount > 0 && (
                          <AgGridStyleTooltip label={`${c.partCount} parts in category`} withArrow>
                            <Text
                              c="dimmed"
                              fz="sm"
                              ml={4}
                              lh={1}
                              mt={2}
                              style={{
                                textWrap: 'nowrap',
                              }}
                            >
                              {c.partCount}
                            </Text>
                          </AgGridStyleTooltip>
                        )}
                      </Flex>
                    </Anchor>
                    {c.hasSubCategories && (
                      <CatalogCategoryNavChild
                        {...props}
                        refresh={refresh ?? reset}
                        parentPartCategory={c}
                        padding={10}
                      />
                    )}
                  </>
                ) : (
                  <Text c="dimmed" lh={1.2} style={{ userSelect: 'none' }}>
                    {c.name}
                  </Text>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Collapse>
      {visible && (
        <TriggerOnVisible
          loaderProps={{ type: 'dots', size: 'sm', m: 0, p: 0, ml: padding + 2 }}
          onVisible={fetchNextPage}
          loading={loading}
          useCollapse
        />
      )}
    </Box>
  );
};

export const CatalogCategoryNav = ({ label = 'Categories', allowCategoryCreation, loading, ...props }: Props) => {
  // Used to force a refresh of the overall categories
  const [needsRerender, setNeedsRerender] = useState(false);

  useEffect(() => {
    if (needsRerender) {
      setTimeout(() => setNeedsRerender(false));
    }
  }, [needsRerender]);

  return (
    <>
      <Flex align="center" justify="space-between" mb="xs">
        <Flex align="center" gap="xs">
          <Text fw="bold">{label}</Text>
          {loading && <Loader size="xs" />}
        </Flex>
        {allowCategoryCreation && (
          <CreateCatalogCategoryModal
            partCatalog={props.partCatalog}
            categoryPath={props.categoryPath}
            onCategoryCreated={(c) => {
              setNeedsRerender(true);
              props.onCategorySelected(c);
            }}
          />
        )}
      </Flex>
      {!needsRerender && (
        <Stack gap={0} flex={1} pr="xs" style={{ overflowY: 'scroll' }}>
          <CatalogCategoryNavChild allowCategoryCreation={allowCategoryCreation} {...props} />
        </Stack>
      )}
    </>
  );
};
